import React, { useEffect, useRef } from "react";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "./ui/dialog";
import { Button } from "./ui/button";
import { BellRing, Check, Clock, RotateCcw } from "lucide-react";
import { IMG_DECUBITO } from "../utils/decubito";

// Alarme de mudança de decúbito: som em loop + tela grande + Wake Lock + notificação.
export const DecubitoAlarm = ({ item, soundOn, onConfirm, onSnooze }) => {
  const audioRef = useRef(null);
  const wakeRef = useRef(null);
  const open = !!item;

  // Som em loop (Web Audio) enquanto soundOn — sirene contínua e forte
  useEffect(() => {
    const stop = () => {
      if (!audioRef.current) return;
      const { ctx, osc, osc2, gain, timer } = audioRef.current;
      clearInterval(timer);
      try { gain.gain.cancelScheduledValues(ctx.currentTime); } catch (e) { /* noop */ }
      try { osc.stop(); } catch (e) { /* noop */ }
      try { osc2 && osc2.stop(); } catch (e) { /* noop */ }
      try { ctx.close(); } catch (e) { /* noop */ }
      audioRef.current = null;
    };
    if (!soundOn) { stop(); return; }
    if (audioRef.current) return () => {};
    const Ctx = window.AudioContext || window.webkitAudioContext;
    if (!Ctx) return () => {};
    const ctx = new Ctx();
    ctx.resume && ctx.resume();
    const gain = ctx.createGain();
    gain.gain.value = 0.95; // volume máximo (limitado pelo volume do dispositivo)
    gain.connect(ctx.destination);
    // duas ondas para timbre mais penetrante
    const osc = ctx.createOscillator();
    osc.type = "sawtooth";
    const osc2 = ctx.createOscillator();
    osc2.type = "square";
    osc.connect(gain); osc2.connect(gain);
    osc.start(); osc2.start();
    // sirene: varre a frequência entre 660 e 1320 Hz a cada 0,45s
    let up = true;
    const sweep = () => {
      try {
        ctx.resume && ctx.resume();
        const t = ctx.currentTime;
        const target = up ? 1320 : 660;
        osc.frequency.linearRampToValueAtTime(target, t + 0.42);
        osc2.frequency.linearRampToValueAtTime(target * 1.5, t + 0.42);
        up = !up;
      } catch (e) { /* noop */ }
    };
    sweep();
    const timer = setInterval(sweep, 450);
    audioRef.current = { ctx, osc, osc2, gain, timer };
    return stop;
  }, [soundOn]);

  // Wake Lock: mantém a tela ligada enquanto o alarme está ativo
  useEffect(() => {
    let cancelled = false;
    const acquire = async () => {
      try {
        if ("wakeLock" in navigator && document.visibilityState === "visible") {
          wakeRef.current = await navigator.wakeLock.request("screen");
        }
      } catch (e) { /* noop */ }
    };
    if (open) acquire();
    const onVis = () => { if (open && document.visibilityState === "visible") acquire(); };
    document.addEventListener("visibilitychange", onVis);
    return () => {
      cancelled = true;
      document.removeEventListener("visibilitychange", onVis);
      try { wakeRef.current && wakeRef.current.release(); wakeRef.current = null; } catch (e) { /* noop */ }
      void cancelled;
    };
  }, [open]);

  // Notificação do navegador quando um novo alarme abre
  useEffect(() => {
    if (open && soundOn && "Notification" in window && Notification.permission === "granted") {
      try {
        new Notification("Mudança de decúbito", {
          body: `${item.nome} → ${item.proxima}`,
          requireInteraction: true,
        });
      } catch (e) { /* noop */ }
    }
    // eslint-disable-next-line
  }, [item && item.rid]);

  if (!item) return null;
  const img = IMG_DECUBITO[item.proxima];

  return (
    <Dialog open={open} onOpenChange={() => {}}>
      <DialogContent aria-describedby={undefined} data-testid="decubito-alarm" className="sm:max-w-md">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2 text-red-600">
            <BellRing className={`w-6 h-6 ${soundOn ? "animate-bounce" : ""}`} /> Mudança de Decúbito
          </DialogTitle>
        </DialogHeader>
        <div className="text-center space-y-3">
          <p className="text-lg font-bold text-slate-800" data-testid="decubito-residente">{item.nome}</p>
          <p className="text-sm text-slate-500">Quarto {item.quarto || "-"} · {item.texto}</p>
          <div className="rounded-2xl overflow-hidden border-2 border-red-200 bg-slate-50">
            {img && <img src={img} alt={item.proxima} className="w-full h-56 object-contain bg-white" data-testid="decubito-img" />}
          </div>
          <div className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-red-50 text-red-700 font-semibold">
            <RotateCcw className="w-4 h-4" /> Mudar para: <span data-testid="decubito-proxima">{item.proxima}</span>
          </div>
        </div>
        <div className="flex flex-col sm:flex-row gap-2 mt-2">
          <Button onClick={() => onSnooze(item.rid)} variant="outline" className="flex-1 gap-2 border-slate-200" data-testid="decubito-snooze">
            <Clock className="w-4 h-4" /> Silenciar 15 min
          </Button>
          <Button onClick={() => onConfirm(item.rid, item.proxima)} className="flex-1 gap-2 bg-emerald-600 hover:bg-emerald-700" data-testid="decubito-confirmar">
            <Check className="w-4 h-4" /> Mudança realizada
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  );
};

export default DecubitoAlarm;
